/** Autoplay panel with spin count selection and stop conditions — uses sprite assets */

import { Container, Graphics, Text, TextStyle, Sprite, Texture, Assets } from 'pixi.js';
import { Logger } from '../utils/Logger';

const logger = new Logger('AutoplayPanel');

export interface AutoplayConfig {
  spinCount: number; // -1 for unlimited
  stopOnAnyWin: boolean;
  stopOnFeature: boolean;
  stopOnBalanceChange: number; // 0 = disabled, otherwise threshold
}

const SPIN_OPTIONS = [10, 25, 50, 100, -1]; // -1 = unlimited
const SPIN_LABELS = ['10', '25', '50', '100', '\u221E'];

const AUTO_ASSETS = {
  bgNormal: 'assets/ui/auto_button_normal.png',
  bgPressed: 'assets/ui/auto_button_pressed.png',
  bgDisabled: 'assets/ui/auto_button_disabled.png',
  iconNormal: 'assets/ui/auto_icon_normal_en.png',
  iconPressed: 'assets/ui/auto_icon_pressed_en.png',
};

/** Autoplay controls panel with configurable spin count and stop conditions */
export class AutoplayPanel extends Container {
  private _isOpen: boolean = false;
  private _panel: Container | null = null;
  private _config: AutoplayConfig = {
    spinCount: 10,
    stopOnAnyWin: false,
    stopOnFeature: true,
    stopOnBalanceChange: 0,
  };
  private _onStartAutoplay: ((config: AutoplayConfig) => void) | null = null;
  private _onStopAutoplay: (() => void) | null = null;
  private _isRunning: boolean = false;
  private _remainingDisplay: Text | null = null;

  // Button container
  private _openBtn: Container;

  // Sprite layers
  private _bgSprite: Sprite | null = null;
  private _iconSprite: Sprite | null = null;
  private _texBgNormal: Texture | null = null;
  private _texBgPressed: Texture | null = null;
  private _texIconNormal: Texture | null = null;

  // Fallback
  private _fallbackBg: Graphics | null = null;
  private _useFallback: boolean = true;

  constructor() {
    super();

    // Auto button
    this._openBtn = new Container();
    this._openBtn.eventMode = 'static';
    this._openBtn.cursor = 'pointer';

    // Fallback graphics (square with gold border, no text)
    this._fallbackBg = new Graphics();
    const fbSize = 60;
    this._fallbackBg.roundRect(-fbSize / 2, -fbSize / 2, fbSize, fbSize, 8);
    this._fallbackBg.fill({ color: 0x333366 });
    this._fallbackBg.stroke({ color: 0xccaa44, width: 2 });
    this._openBtn.addChild(this._fallbackBg);

    this._openBtn.on('pointerdown', () => {
      if (this._isRunning) {
        this.stopAutoplay();
      } else {
        this.togglePanel();
      }
    });
    this.addChild(this._openBtn);

    // Remaining spins display
    this._remainingDisplay = new Text({
      text: '',
      style: new TextStyle({
        fontFamily: 'Arial Black',
        fontSize: 14,
        fill: 0xf7b733,
      }),
    });
    this._remainingDisplay.anchor.set(0.5, 0);
    this._remainingDisplay.y = 22;
    this.addChild(this._remainingDisplay);

    // Load auto button sprite assets
    this._loadAssets();
  }

  get isOpen(): boolean {
    return this._isOpen;
  }

  get isRunning(): boolean {
    return this._isRunning;
  }

  get config(): AutoplayConfig {
    return { ...this._config };
  }

  /** Set callback for starting autoplay */
  onStartAutoplay(callback: (config: AutoplayConfig) => void): void {
    this._onStartAutoplay = callback;
  }

  /** Set callback for stopping autoplay */
  onStopAutoplay(callback: () => void): void {
    this._onStopAutoplay = callback;
  }

  /** Set running state and update display */
  setRunning(running: boolean, remaining?: number): void {
    this._isRunning = running;
    if (this._remainingDisplay) {
      if (running && remaining !== undefined) {
        this._remainingDisplay.text = remaining === -1 ? '\u221E' : String(remaining);
      } else {
        this._remainingDisplay.text = '';
      }
    }

    // Update button appearance
    if (this._useFallback) {
      this._updateFallback();
    } else {
      this._updateSpriteVisuals();
    }
  }

  /** Update remaining spins count */
  updateRemaining(remaining: number): void {
    if (this._remainingDisplay) {
      this._remainingDisplay.text = remaining === -1 ? '\u221E' : String(remaining);
    }
  }

  /** Toggle the settings panel */
  togglePanel(): void {
    this._isOpen = !this._isOpen;
    if (this._isOpen) {
      this._showPanel();
    } else {
      this._hidePanel();
    }
  }

  /** Stop autoplay */
  stopAutoplay(): void {
    this._isRunning = false;
    this.setRunning(false);
    if (this._onStopAutoplay) {
      this._onStopAutoplay();
    }
    logger.info('Autoplay stopped by user');
  }

  private _updateFallback(): void {
    if (!this._fallbackBg) return;
    const fbSize = 60;
    this._fallbackBg.clear();
    if (this._isRunning) {
      this._fallbackBg.roundRect(-fbSize / 2, -fbSize / 2, fbSize, fbSize, 8);
      this._fallbackBg.fill({ color: 0x993333 });
      this._fallbackBg.stroke({ color: 0xff6666, width: 2 });
    } else {
      this._fallbackBg.roundRect(-fbSize / 2, -fbSize / 2, fbSize, fbSize, 8);
      this._fallbackBg.fill({ color: 0x333366 });
      this._fallbackBg.stroke({ color: 0xccaa44, width: 2 });
    }
  }

  private _updateSpriteVisuals(): void {
    // When running, show stop state; otherwise show normal auto button
    if (this._bgSprite && this._texBgNormal) {
      this._bgSprite.texture = this._texBgNormal;
      this._bgSprite.visible = !this._isRunning;
    }
    if (this._iconSprite && this._texIconNormal) {
      this._iconSprite.visible = !this._isRunning;
    }
    // Show fallback-style stop button when running
    if (this._isRunning) {
      if (!this._fallbackBg) {
        this._fallbackBg = new Graphics();
        this._openBtn.addChild(this._fallbackBg);
      }
      this._fallbackBg.visible = true;
      const fbSize = 60;
      this._fallbackBg.clear();
      this._fallbackBg.roundRect(-fbSize / 2, -fbSize / 2, fbSize, fbSize, 8);
      this._fallbackBg.fill({ color: 0x993333 });
      this._fallbackBg.stroke({ color: 0xff6666, width: 2 });
    } else {
      if (this._fallbackBg) this._fallbackBg.visible = false;
    }
  }

  private _showPanel(): void {
    if (this._panel) return;

    const panel = new Container();
    panel.eventMode = 'static';

    // Panel background
    const bg = new Graphics();
    bg.roundRect(0, 0, 280, 320, 12);
    bg.fill({ color: 0x1a1a3a, alpha: 0.95 });
    bg.stroke({ color: 0x4444aa, width: 2 });
    panel.addChild(bg);

    // Title
    const titleText = new Text({
      text: 'AUTOPLAY',
      style: new TextStyle({
        fontFamily: 'Arial Black',
        fontSize: 20,
        fill: 0xf7b733,
      }),
    });
    titleText.anchor.set(0.5, 0);
    titleText.x = 140;
    titleText.y = 15;
    panel.addChild(titleText);

    // Spin count options
    const spinLabel = new Text({
      text: 'Number of Spins:',
      style: new TextStyle({ fontFamily: 'Arial', fontSize: 14, fill: 0xcccccc }),
    });
    spinLabel.x = 20;
    spinLabel.y = 50;
    panel.addChild(spinLabel);

    for (let i = 0; i < SPIN_OPTIONS.length; i++) {
      const option = this._createSpinOption(
        SPIN_LABELS[i],
        SPIN_OPTIONS[i],
        20 + i * 52,
        75,
      );
      panel.addChild(option);
    }

    // Stop conditions
    const condLabel = new Text({
      text: 'Stop Conditions:',
      style: new TextStyle({ fontFamily: 'Arial', fontSize: 14, fill: 0xcccccc }),
    });
    condLabel.x = 20;
    condLabel.y = 130;
    panel.addChild(condLabel);

    const winToggle = this._createToggle('Stop on any win', this._config.stopOnAnyWin, 20, 155, (val) => {
      this._config.stopOnAnyWin = val;
    });
    panel.addChild(winToggle);

    const featureToggle = this._createToggle('Stop on feature', this._config.stopOnFeature, 20, 190, (val) => {
      this._config.stopOnFeature = val;
    });
    panel.addChild(featureToggle);

    // Start button
    const startBtn = new Container();
    startBtn.eventMode = 'static';
    startBtn.cursor = 'pointer';
    startBtn.x = 140;
    startBtn.y = 260;

    const startBg = new Graphics();
    startBg.roundRect(-80, -22, 160, 44, 10);
    startBg.fill({ color: 0x22aa44 });
    startBg.stroke({ color: 0xffffff, width: 2 });
    startBtn.addChild(startBg);

    const startLabel = new Text({
      text: 'START',
      style: new TextStyle({
        fontFamily: 'Arial Black',
        fontSize: 20,
        fill: 0xffffff,
      }),
    });
    startLabel.anchor.set(0.5);
    startBtn.addChild(startLabel);

    startBtn.on('pointerdown', () => {
      this._hidePanel();
      this._isOpen = false;
      this._isRunning = true;
      this.setRunning(true, this._config.spinCount);
      if (this._onStartAutoplay) {
        this._onStartAutoplay({ ...this._config });
      }
      logger.info(`Autoplay started: ${this._config.spinCount} spins`);
    });
    panel.addChild(startBtn);

    // Close button
    const closeBtn = new Container();
    closeBtn.eventMode = 'static';
    closeBtn.cursor = 'pointer';
    closeBtn.x = 255;
    closeBtn.y = 15;

    const closeBg = new Graphics();
    closeBg.circle(0, 0, 14);
    closeBg.fill({ color: 0xcc3333 });
    closeBtn.addChild(closeBg);

    const closeX = new Text({
      text: 'X',
      style: new TextStyle({ fontFamily: 'Arial', fontSize: 14, fill: 0xffffff }),
    });
    closeX.anchor.set(0.5);
    closeBtn.addChild(closeX);

    closeBtn.on('pointerdown', () => {
      this._hidePanel();
      this._isOpen = false;
    });
    panel.addChild(closeBtn);

    // Position above the button
    panel.x = -140;
    panel.y = -350;

    this._panel = panel;
    this.addChild(panel);
  }

  private _hidePanel(): void {
    if (this._panel) {
      this.removeChild(this._panel);
      this._panel.destroy({ children: true });
      this._panel = null;
    }
  }

  private _createSpinOption(label: string, value: number, x: number, y: number): Container {
    const container = new Container();
    container.eventMode = 'static';
    container.cursor = 'pointer';
    container.x = x;
    container.y = y;

    const isSelected = this._config.spinCount === value;

    const bg = new Graphics();
    bg.roundRect(0, 0, 46, 40, 6);
    bg.fill({ color: isSelected ? 0x22aa44 : 0x2a2a5a });
    bg.stroke({ color: isSelected ? 0xffffff : 0x5555bb, width: 1 });
    container.addChild(bg);

    const text = new Text({
      text: label,
      style: new TextStyle({
        fontFamily: 'Arial',
        fontSize: 16,
        fill: 0xffffff,
      }),
    });
    text.anchor.set(0.5);
    text.x = 23;
    text.y = 20;
    container.addChild(text);

    container.on('pointerdown', () => {
      this._config.spinCount = value;
      // Rebuild panel to update selection
      this._hidePanel();
      this._showPanel();
    });

    return container;
  }

  private _createToggle(
    label: string,
    initial: boolean,
    x: number,
    y: number,
    onChange: (val: boolean) => void,
  ): Container {
    const container = new Container();
    container.eventMode = 'static';
    container.cursor = 'pointer';
    container.x = x;
    container.y = y;

    let isOn = initial;

    const checkbox = new Graphics();
    this._drawCheckbox(checkbox, isOn);
    container.addChild(checkbox);

    const text = new Text({
      text: label,
      style: new TextStyle({ fontFamily: 'Arial', fontSize: 14, fill: 0xcccccc }),
    });
    text.x = 30;
    text.y = 2;
    container.addChild(text);

    container.on('pointerdown', () => {
      isOn = !isOn;
      this._drawCheckbox(checkbox, isOn);
      onChange(isOn);
    });

    return container;
  }

  private _drawCheckbox(g: Graphics, checked: boolean): void {
    g.clear();
    g.roundRect(0, 0, 22, 22, 4);
    g.fill({ color: checked ? 0x22aa44 : 0x333355 });
    g.stroke({ color: 0x6666aa, width: 2 });
    if (checked) {
      // Checkmark
      g.moveTo(5, 11);
      g.lineTo(9, 16);
      g.lineTo(17, 5);
      g.stroke({ color: 0xffffff, width: 2 });
    }
  }

  private async _loadAssets(): Promise<void> {
    try {
      const [bgNorm, bgPress, icNorm, icPress] = await Promise.all([
        Assets.load<Texture>(AUTO_ASSETS.bgNormal).catch(() => null),
        Assets.load<Texture>(AUTO_ASSETS.bgPressed).catch(() => null),
        Assets.load<Texture>(AUTO_ASSETS.iconNormal).catch(() => null),
        Assets.load<Texture>(AUTO_ASSETS.iconPressed).catch(() => null),
      ]);

      if (bgNorm) {
        this._texBgNormal = bgNorm;
        this._texBgPressed = bgPress;
        this._texIconNormal = icNorm;

        // Create layered sprites
        this._bgSprite = new Sprite(bgNorm);
        this._bgSprite.anchor.set(0.5);
        this._openBtn.addChild(this._bgSprite);

        if (icNorm) {
          this._iconSprite = new Sprite(icNorm);
          this._iconSprite.anchor.set(0.5);
          this._openBtn.addChild(this._iconSprite);
        }

        // Remove fallback
        if (this._fallbackBg) {
          this._openBtn.removeChild(this._fallbackBg);
          this._fallbackBg.destroy();
          this._fallbackBg = null;
        }
        this._useFallback = false;

        logger.info('Auto button sprites loaded (layered bg + icon)');
      } else {
        logger.debug('Auto button sprite assets not available, using fallback');
      }
    } catch (err) {
      logger.debug(`Auto button assets not loaded: ${err}`);
    }
  }
}
